/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { Boton, useIsMobile, Titulo, tituloBase } from "../../shared";

import CampoGeneradorEtiquetas from "./CampoGeneradorEtiquetas";
import DropdownGeneradorEtiquetas from "./DropdownGeneradorEtiquetas";
import DisenoEtiqueta from "./DisenoEtiqueta";
import { Card } from "primereact/card";
import * as estilos from "./estilosGenerador";

// Preguntas sí/no por grupo. El id es el índice en `configuracion` que lee
// DisenoEtiqueta: no cambiarlos (por eso no van en orden).
const GRUPOS = [
  {
    titulo: "Empaque",
    preguntas: [
      { id: "25", titulo: "¿Viene empacado en un material plástico flexible?" },
      { id: "26", titulo: "¿El embalaje contiene grampas?" }
    ]
  },
  {
    titulo: "Clasificación",
    preguntas: [
      { id: "16", titulo: "¿Son patines o patineta?" },
      { id: "30", titulo: "¿Es o contiene una pelota pequeña?" },
      { id: "31", titulo: "¿Es o contiene una bolita?" },
      { id: "1", titulo: "¿Es un juguete acuático?" },
      { id: "2", titulo: "¿Es un globo?" },
      { id: "19", titulo: "¿Es una cometa?" },
      { id: "20", titulo: "¿Es una bicicleta de juguete?" },
      { id: "22", titulo: "¿Es un tobogán, columpio, anillas, trapecios, cuerda o análogos fijado sobre soportes?" }
    ]
  },
  {
    titulo: "Características",
    preguntas: [
      { id: "24", titulo: "¿Está destinado a soportar el peso de un niño?" },
      { id: "3", titulo: "¿Tiene proyectiles?" },
      { id: "18", titulo: "¿Tiene fulminantes?" },
      { id: "23", titulo: "¿Genera un nivel elevado de ruido?" },
      { id: "17", titulo: "¿Está destinado a fijarse a ambos lados de cunas, camas o coches de niños?" },
      { id: "21", titulo: "¿Tiene imitación de elementos de protección?" }
    ]
  },
  {
    titulo: "Juegos de modelar",
    preguntas: [
      { id: "4", titulo: "¿Es un juego de modelar con escayola?" },
      { id: "5", titulo: "¿Tiene materiales cerámicos o esmaltes vítreos acompañando un taller en miniatura?" },
      { id: "6", titulo: "¿Es de PVC plastificado para modelar y endurecer al horno?" },
      { id: "7", titulo: "¿Es un juego de modelar con plástico?" },
      { id: "8", titulo: "¿Es un juego de inclusión o embutido?" },
      { id: "9", titulo: "¿Es un juego de revelado fotográfico?" }
    ]
  },
  {
    titulo: "Adhesivos y pinturas",
    preguntas: [
      { id: "10", titulo: "¿Incluye adhesivos, pinturas, lacas, barnices, diluyentes o productos de limpieza?" },
      { id: "11", titulo: "¿Es una barra adhesiva para papel?" },
      { id: "12", titulo: "¿Tiene adhesivos especiales?" },
      { id: "13", titulo: "¿Son etiquetas o figuras adhesivas?" },
      { id: "14", titulo: "¿Son pinturas o lacas con base acuosa?" },
      { id: "15", titulo: "¿Son pinturas con disolventes, lacas, diluyente o agentes limpiadores?" }
    ]
  }
];

const EDADES = [
  { value: "0", etiqueta: "Cualquier edad" },
  { value: "1", etiqueta: "Mayores de 3 años" },
  { value: "2", etiqueta: "Mayores de 5 años" },
  { value: "3", etiqueta: "Mayores de 8 años" },
  { value: "4", etiqueta: "Mayores de 10 años" },
  { value: "5", etiqueta: "Mayores de 12 años" }
];

const irA = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function GeneradorEtiquetasJuguetes() {
  // Título de la pestaña, como en el admin
  useEffect(() => {
    document.title = "Generador de etiquetas";
    return () => (document.title = tituloBase());
  }, []);

  const esMovil = useIsMobile(991);
  // configuracion guarda la configuración actual de la etiqueta, cada índice es un dato
  // En el formulario se tiene que respetar en id de cada parámetro
  const [configuracion, setConfiguracion] = useState(["0"]);

  function cambiar(id, valor) {
    setConfiguracion((actual) => {
      const nueva = [...actual];
      nueva[id] = valor;
      return nueva;
    });
  }
  const cambioConfiguracion = (parametro, id) => cambiar(id, parametro.target.checked);
  const cambioConfiguracionDropdown = (parametro, id) => cambiar(id, parametro.target.value);

  const pregunta = (p) => (
    <CampoGeneradorEtiquetas
      key={p.id}
      id={p.id}
      titulo={p.titulo}
      valor={configuracion[p.id]}
      cambioConfiguracion={cambioConfiguracion}
    />
  );

  return (
    <>
      <Titulo
        // Flecha al Inicio, o a donde indique ?volver=
        urlVolver="/"
        subtitulo="Visualice y descargue el diseño de las etiquetas de seguridad para consumidores de juguetes."
      >
        Generador de etiquetas de seguridad para juguetes
      </Titulo>
      <Card css={{ marginBottom: "1em" }}>
        <Titulo>
          Instrucciones
        </Titulo>
        <div css={{ fontSize: "0.9rem", color: "var(--p-texto)", "& p": { margin: "0 0 0.75rem" } }}>
          <p>
            Ingrese las características del juguete y verá la etiqueta formándose{" "}
            {esMovil ? "al final de la página" : "a la derecha"}. Puede usar ese diseño para validar
            una etiqueta existente o realizar un diseño propio, y descargarlo con el botón debajo
            de la etiqueta.
          </p>
          <p>
            Las leyendas de advertencia deben ser consignadas en español, deben ser exhibidas en la
            cara principal del envase en colores contrastantes y destacadas de otras informaciones y
            diseños. Los caracteres no deben ser inferiores de 2 milímetros.
          </p>
          <p css={{ color: "var(--rojo)", fontWeight: 600, marginBottom: "0 !important" }}>
            IMPORTANTE: algunas características del juguete pueden requerir un rango etario
            específico, el diseñador no lo hace automáticamente. Lea con cuidado las advertencias.
          </p>
        </div>
      </Card>

      <div css={estilos.grilla}>
        <div css={{ minWidth: 0 }}>
          <Card css={{ marginBottom: "1em" }}>
            <Titulo>
              Datos del importador y código
            </Titulo>
            <CampoGeneradorEtiquetas
              id="27"
              titulo="Mostrar datos del importador"
              valor={configuracion[27]}
              cambioConfiguracion={cambioConfiguracion}
            />
            <div css={estilos.filaControl}>
              <label htmlFor="codigo-juguete" css={estilos.pregunta}>
                Código del juguete
              </label>
              <InputText
                id="codigo-juguete"
                css={estilos.control}
                onChange={(e) => cambiar(29, e.target.value)}
              />
            </div>
          </Card>

          <Card css={{ marginBottom: "1em" }}>
            <Titulo botones={
              esMovil && (
                <Boton texto chico callback={() => irA("modelo-etiqueta")}>
                  Ver etiqueta <i className="pi pi-arrow-down" css={{ marginLeft: "0.35em" }} />
                </Boton>
              )
            }>
              Características del juguete
            </Titulo>
            <DropdownGeneradorEtiquetas
              id="0"
              titulo="Rango etario recomendado"
              valor={configuracion[0]}
              opciones={EDADES}
              cambioConfiguracion={cambioConfiguracionDropdown}
            />
            {pregunta({ id: "28", titulo: "¿Contiene partes pequeñas?" })}
            {GRUPOS.map((g) => (
              <div key={g.titulo}>
                <h3 css={estilos.grupo}>{g.titulo}</h3>
                {g.preguntas.map(pregunta)}
              </div>
            ))}
          </Card>
        </div>

        <div id="modelo-etiqueta" css={estilos.modelo}>
          <DisenoEtiqueta configuracion={configuracion} />
          {esMovil && (
            <div css={{ marginTop: "0.75rem" }}>
              <Boton texto chico callback={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
                <i className="pi pi-arrow-up" css={{ marginRight: "0.35em" }} />
                Volver a las características
              </Boton>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
